import type { OpenApiDocument } from '../api/types';

export type HttpMethod = 'get' | 'post' | 'patch' | 'put' | 'delete';
export interface Endpoint {
  readonly method: HttpMethod;
  readonly path: string;
  readonly summary: string;
  readonly tag: string;
  readonly params: readonly string[];
  readonly hasBody: boolean;
}

const METHOD_ORDER: readonly HttpMethod[] = ['get', 'post', 'patch', 'put', 'delete'];

/** Flattens the document into one row per operation, grouped by tag then path then method. */
export function listEndpoints(doc: OpenApiDocument): Endpoint[] {
  return Object.entries(doc.paths)
    .flatMap(([path, ops]) =>
      METHOD_ORDER.flatMap((method) => {
        const op = ops[method];
        return op
          ? [
              {
                method,
                path,
                summary: op.summary,
                tag: op.tags[0] ?? 'misc',
                params: op.parameters.map((p) => p.name),
                hasBody: op.requestBody !== undefined,
              },
            ]
          : [];
      }),
    )
    .toSorted((a, b) => a.tag.localeCompare(b.tag) || a.path.localeCompare(b.path));
}

/** `/v1/contacts/{id}/tags/{tag}` + {id: 'c1', tag: 'vip'} -> `/v1/contacts/c1/tags/vip`. */
export function fillPath(template: string, params: Readonly<Record<string, string>>): string {
  return template.replace(/\{([A-Za-z_]+)\}/g, (_m, name: string) =>
    encodeURIComponent(params[name] ?? ''),
  );
}

export interface ConsoleRequest {
  readonly url: string;
  readonly init: { method: string; headers: Record<string, string>; body?: string };
}

/** Pure: what the console will send. The body is validated as JSON here so a typo is a local error. */
export function buildRequest(input: {
  readonly endpoint: Endpoint;
  readonly apiKey: string;
  readonly params: Readonly<Record<string, string>>;
  readonly query: string;
  readonly body: string;
}): ConsoleRequest | { readonly error: string } {
  if (input.apiKey.trim() === '') return { error: 'paste a workspace API key first' };
  const missing = input.endpoint.params.find((p) => (input.params[p] ?? '').trim() === '');
  if (missing) return { error: `path parameter "${missing}" is required` };
  const q = input.query.trim().replace(/^\?/, '');
  const url = fillPath(input.endpoint.path, input.params) + (q ? `?${q}` : '');
  const headers: Record<string, string> = { authorization: `Bearer ${input.apiKey.trim()}` };
  if (!input.endpoint.hasBody)
    return { url, init: { method: input.endpoint.method.toUpperCase(), headers } };
  const raw = input.body.trim() === '' ? '{}' : input.body;
  try {
    JSON.parse(raw);
  } catch {
    return { error: 'body is not valid JSON' };
  }
  return {
    url,
    init: {
      method: input.endpoint.method.toUpperCase(),
      headers: { ...headers, 'content-type': 'application/json' },
      body: raw,
    },
  };
}
